import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DirectLink, Sale } from '@src/core/buyerApiClient';
import { RootState, AppDispatch, getClient, bearerAuth } from '@src/core/store';
import { setupCart } from './cart';
import { resetSale, fetchSaleRequiredFormInputs } from './sale';
import { clearBuyer } from './auth';

export interface DirectLinkState {
  directLink?: DirectLink;
  error?: string;
  status: 'idle' | 'loading' | 'loaded' | 'error' | 'finished';
}

const initialState: DirectLinkState = {
  directLink: undefined,
  error: undefined,
  status: 'idle',
};

// Reducers

export const directLinkSlice = createSlice({
  name: 'directLink',
  initialState,
  reducers: {
    setDirectLink: (state: DirectLinkState, action: PayloadAction<DirectLink | undefined>) => {
      state.directLink = action.payload;
    },
    setProcessedDirectLink: (state: DirectLinkState, action: PayloadAction<DirectLink, string>) => {
      state.directLink = action.payload;
    },
    setError: (state: DirectLinkState, action: PayloadAction<string | undefined>) => {
      state.error = action.payload;
    },
    setStatus: (state: DirectLinkState, action: PayloadAction<DirectLinkState['status']>) => {
      state.status = action.payload;
    },
    resetDirectLink: () => initialState,
  },
});

// Selectors

export const directLinkStateSelector = (state: RootState) => state.directLink;

// Actions

export const setupDirectLink = () => async (dispatch: AppDispatch) => {
  dispatch(resetSale());
  dispatch(resetDirectLink());
  dispatch(clearBuyer());
};

export const loadDirectLink = (directLinkCode: string) => async (dispatch: AppDispatch, getState: () => RootState) => {
  dispatch(setStatus('loading'));

  try {
    const { data: link } = await getClient(getState()).directLinks.getDirectLink(directLinkCode);
    dispatch(setDirectLink(link));

    if (link.type === 'cart') {
      dispatch(setupCart(directLinkCode));
    }

    dispatch(setStatus('loaded'));
  } catch (error: any) {
    dispatch(setError('Página não encontrada. Por favor, confira o link enviado para você e tente novamente.'));

    dispatch(setStatus('error'));
  }
};

export const setupDirectLinkCheckout = (id: Sale['id']) => async (dispatch: AppDispatch, getState: () => RootState) => {
  const state = getState();
  const directLinkState = directLinkStateSelector(state);
  const {
    auth: { buyerToken, buyer },
  } = state;

  if (!buyerToken || !buyer) {
    return;
  }

  if (buyer.id !== directLinkState.directLink?.customerId) {
    dispatch(setError('Você não possui permissão para acessar esta venda.'));
  }

  const { data: sale } = await getClient(state).sales.getSale(id, bearerAuth(buyerToken));

  dispatch(fetchSaleRequiredFormInputs(sale));
};

export const { setDirectLink, setProcessedDirectLink, setStatus, setError, resetDirectLink } = directLinkSlice.actions;

export default directLinkSlice.reducer;
